import React from "react";
import PropTypes from "prop-types";
import SEO from "../../components/seo";

const introText = {
	fontFamily: "var(--body-font)",
	fontSize: 17,
	lineHeight: 1.7,
	color: "var(--text-muted)",
	margin: "0 0 48px",
};

const sectionHeading = {
	fontFamily: "var(--heading-font)",
	fontWeight: 700,
	fontSize: 22,
	lineHeight: 1.3,
	color: "var(--text)",
	margin: 0,
	borderLeft: "4px solid var(--primary)",
	paddingLeft: 16,
};

const bodyText = {
	fontFamily: "var(--body-font)",
	fontSize: 15.5,
	lineHeight: 1.75,
	color: "var(--text-muted)",
	margin: "0 0 16px",
};

const ServiceSection = ({ title, children, last }) => (
	<section
		style={{
			paddingBottom: 32,
			marginBottom: 32,
			borderBottom: last ? "none" : "1px solid var(--border)",
		}}
	>
		<h2 style={sectionHeading}>{title}</h2>
		<div style={{ paddingLeft: 20, marginTop: 18 }}>{children}</div>
	</section>
);

ServiceSection.propTypes = {
	title: PropTypes.string.isRequired,
	children: PropTypes.node.isRequired,
	last: PropTypes.bool,
};

ServiceSection.defaultProps = {
	last: false,
};

const Services = () => {
	return (
		<div>
			<SEO
				title="Our Services Gastroenterology, Hepatology, Rheumatology and Endoscopy"
				keywords={[
					`Gastroenterology services`,
					`Endoscopy services`,
					`Rheumatology services`,
					`Gastroenterologist`,
					`Hepatologist`,
					`Rheumatologist`,
					`Dr Frank`,
					`Dr Brennan`,
					`Dr Spiro`,
					`Dr Raftopoulos`,
					`Dr Ian Yusoff`,
					`Dr Dev Segarajasingam`,
					`Dr Sam Rao`,
					`Dr Claire Gordon`,
					`Dr Duy Pham`,
					`Dr Kelly Morgan`,
				]}
				description="Gastroenterology, hepatology and rheumatology services on offer at Advanced GI WA"
			/>

			<p style={{ ...introText, marginTop: 40 }}>
				We offer a wide range of services including consultation in general gastroenterology, inflammatory bowel
				disease, hepatology and rheumatology. In addition we also offer all aspects of both general and
				interventional endoscopy. Below is a list of some of these procedures, which will be discussed in more
				detail at the time of your consultation.
			</p>

			<ServiceSection title="Upper Endoscopy / Gastroscopy">
				<p style={bodyText}>
					Gastroscopy (upper endoscopy) is a procedure that uses a flexible endoscope to see inside the upper
					digestive tract – including the oesophagus, stomach and the first part of the small intestine. During
					an upper endoscopy a thin flexible tube is carefully fed down the oesophagus into the stomach and the
					small intestine. A small camera on the endoscope transmits an image to a monitor, allowing close
					examination of the intestinal lining.
				</p>
				<p style={bodyText}>
					It is used to determine the cause of abdominal pain, nausea, vomiting, swallowing difficulties, gastric
					reflux, unexplained weight loss, anaemia or bleeding in the upper digestive tract. Upper endoscopy is
					also used to remove stuck objects (including food), treat conditions such as bleeding ulcers and
					strictures and biopsy tissue. During a biopsy, a small piece of tissue is removed for later
					examination with a microscope.
				</p>
			</ServiceSection>

			<ServiceSection title="Colonoscopy">
				<p style={bodyText}>
					A colonoscopy is a procedure that examines the large bowel using a flexible tube with a digital
					camera. The image from the camera appears on a monitor to provide magnified view of the colon. The
					colonoscope is gently inserted through the anus and up into the colon. This procedure can be used to
					look for colon polyps or cancer of the colon and can be use to treat known advanced polyps of the
					bowel.
				</p>
			</ServiceSection>

			<ServiceSection title="Endoscopic Retrograde Cholangiopancreatography (ERCP)">
				<p style={bodyText}>
					Endoscopic retrograde cholangiopancreatography (ERCP) is a technique that combines the use of
					endoscopy and fluoroscopy to diagnose and treat certain problems of the digestive system such as
					gallstones, inflammatory strictures (scars) or cancer.
				</p>
				<p style={bodyText}>
					The patient is sedated or anaesthetised. During the procedure a thin telescope (endoscope) is inserted
					down the throat to allow the doctor to see inside the digestive system.
				</p>
				<p style={bodyText}>
					The doctor then injects a dye into the bile duct or pancreatic duct so it can be seen on an x-ray
					(fluroscopy) while the necessary procedure is performed.
				</p>
			</ServiceSection>

			<ServiceSection title="Endoscopic Ultrasonography (EUS)">
				<p style={bodyText}>
					Endoscopic ultrasonography (EUS) allows your doctor to examine esophageal and stomach linings as well
					the walls of your upper and lower gastrointestinat tract. The doctor will use a thin, flexible tube
					called an endoscope that has built-in miniture ultrasound probe. The endoscope will passed through
					your mouth or anus to the area to be examined. The ultrasound is then used to create visual images of
					the digestive tract.
				</p>
			</ServiceSection>

			<ServiceSection title="Capsule Endoscopy">
				<p style={bodyText}>
					This procedure involves the ingestion of a small capsule that contains a tiny camera, which can take
					2-4 images per second for eight hours. It has been specifically developed to view the small bowel.
				</p>
				<p style={bodyText}>
					Capsule endoscopy is used to try and pinpoint bleeding in hidden areas in the small bowel. It is also
					useful for tracking small bowel tumours and obscure abdominal pain.
				</p>
				<p style={bodyText}>
					The capsule is swallowed and travels, just like a piece of food, through the stomach, small bowel and
					into the large bowel. Recording probes, taped onto the abdomen, track the progress of the capsule as
					it makes its way through the gastrointestinal system. The images obtained by the capsule are
					transmitted to a data recorder worn in a harness around the waist.
				</p>
			</ServiceSection>

			<ServiceSection title="Advanced Endoscopic Resection">
				<p style={bodyText}>
					This procedure involves the removal of pre-malignant and early malignant lesions of the oesophagus,
					stomach, duodenunum or colon. These new techniques have enabled the provision of surgery sparing
					therapies that are most often performed as a day procedure with much lower morbidity and mortailiy
					when compared to the traditional surgical alternatives.
				</p>
				<p style={bodyText}>
					These specialised techniques have been brought back to Western Australia following overseas
					fellowships in high volume, expert centres by several of the doctors at Advanced GI WA.
				</p>
				<p style={bodyText}>
					Exact specifics relating to these specialised procedures will be discuss in greater detail during the
					consultative process.
				</p>
			</ServiceSection>

			<ServiceSection title="Per Oral Endoscopic Myotomy">
				<p style={bodyText}>
					This procedure is performed for the treatment of several oesophageal motility disorders including
					Achalasia, Diffuse Oesophageal Spasm and Jack Hammer Oesophagus. It involves the creation of a tunnel
					in the wall of the oesophagus along the length of muscle and then with a small needle knife, the
					muscle is cut followed by closure of the hole. Traditionally this was offered through a surgical
					approach however has now quickly become the standard of care in many centres around the world
					including Australia.
				</p>
				<p style={bodyText}>
					Advanced GI WA was the first to introduce this technique into Western Australia and has been
					successfully performing this since 2017.
				</p>
			</ServiceSection>

			<ServiceSection title="Radiofrequency Ablation" last>
				<p style={bodyText}>
					This procedure is used for the eradication and treatment of Barrett&apos;s Oesophagus. It uses a
					combination of balloon&apos;s and flat paddles that apply a current to the lining of the Oesophagus
					burning it and thereby destroying the abnormal Barrett Cells and thereby allowing new healthy cells
					to grow back in its place.
				</p>
				<p style={{ ...bodyText, marginBottom: 0 }}>
					Most private health funds now fully cover the costs of this treatment in private as it allows
					treating doctors to provide an alternative to major thoracic surgery which comes at a much high risk
					of mortality and long term morbidity.
				</p>
			</ServiceSection>
		</div>
	);
};

export default Services;
